import React, { Component } from 'react';
import { Box, Typography } from '@mui/material';
import SectionTitle from './SectionTitle';
import CustomLightButton from '../../../../../components/CustomLightButton';
import RadioButtonGroup from '../../../../../components/RadioButtonGroup';
import PaginationWrapper from '../../../../../components/PaginationWrapper';
import DeletableChip from '../../../../../components/DeletableChip';
import DeletableNumericInput from '../../../../../components/DeletableNumericInput';
import { ALERT_SEVERITY } from '../../../../../components/AlertDisplay2';

export const CATEGORY_RESTRICTION_SELECTION_TYPES = {
    NO_RESTRICTIONS: 'NO_RESTRICTION_CATEGORY',
    ONLY_SELECTED: 'ONLY_SELECTED_CATEGORIES',
};

export const CATEGORY_SCOPE_TYPE = {
    VALID_FOR: 'VALID_FOR',
    NOT_VALID_FOR: 'NOT_VALID_FOR',
};

class CategoryConfigSection extends Component {
    constructor(props) {
        super(props);

        this.categorySelectionTypes = [
            { key: CATEGORY_RESTRICTION_SELECTION_TYPES.NO_RESTRICTIONS, label: 'Keine Einschränkung' },
            { key: CATEGORY_RESTRICTION_SELECTION_TYPES.ONLY_SELECTED, label: 'Nur ausgewählte Kategorien' },
        ];

        this.categoryScopeTypes = [
            { key: CATEGORY_SCOPE_TYPE.VALID_FOR, label: 'Gültig für' },
            { key: CATEGORY_SCOPE_TYPE.NOT_VALID_FOR, label: 'Nicht gültig für' },
        ];

        this.state = {
            mustHaveMinQty: (props.selectedCategories ?? []).some((cat) => cat.minQty != null) || false,
        };
    }

    componentDidUpdate(prevProps) {
        const { selectedCategories = [] } = this.props;

        if (prevProps.selectedCategories !== selectedCategories) {
            const mustHaveMinQty = selectedCategories.some((cat) => cat.minQty != null);
            if (mustHaveMinQty !== this.state.mustHaveMinQty) {
                this.setState({ mustHaveMinQty });
            }
        }
    }

    normalize = (s) => (s ?? '').toString().toLowerCase().trim();

    deleteById = (id) => {
        const {
            selectedCategories,
            setSelectedCategories,
            setSelectedOptions,
            selectedOptions,
            editMode,
        } = this.props;

        const myCategories = Array.isArray(selectedCategories) ? [...selectedCategories] : [];
        const index = myCategories.findIndex((cat) => this.normalize(cat?.id) === this.normalize(id));

        if (index >= 0 && index < myCategories.length) {
            if (editMode) {
                myCategories[index] = { ...myCategories[index], deleted: true };
            } else {
                myCategories.splice(index, 1);
            }

            const deletedCount = myCategories.filter((cat) => cat.deleted).length;

            if (deletedCount === myCategories.length) {
                myCategories.forEach((cat) => {
                    delete cat.deleted;
                });

                setSelectedCategories?.(myCategories);
                setSelectedOptions?.({
                    ...selectedOptions,
                    [editMode ? 'newSelectionType' : 'selectionType']:
                        CATEGORY_RESTRICTION_SELECTION_TYPES.NO_RESTRICTIONS,
                });
            } else {
                setSelectedCategories?.(myCategories);
            }
        }
    };

    reselectById(id) {
        const { selectedCategories, setSelectedCategories, editMode } = this.props;
        const myCategories = Array.isArray(selectedCategories) ? [...selectedCategories] : [];
        const index = myCategories.findIndex((cat) => this.normalize(cat?.id) === this.normalize(id));

        if (index >= 0 && index < myCategories.length) {
            if (editMode) {
                const { deleted, ...rest } = myCategories[index];
                myCategories[index] = rest;
                setSelectedCategories?.(myCategories);
            }
        }
    }

    render() {
        const {
            selectedOptions,
            setSelectedOptions,
            selectedCategories = [],
            editMode,
            setSelectedCategories,
            showAlert,
        } = this.props;

        const mySelectionType =
            selectedOptions?.newSelectionType ?? selectedOptions?.selectionType;

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <SectionTitle
                    icon={require('../../../../../assets/icons/categories.png')}
                    label="Kategorien"
                />

                <RadioButtonGroup
                    items={this.categorySelectionTypes}
                    selectedKey={mySelectionType}
                    onSelect={(item) => {
                        if (editMode) {
                            if (item === CATEGORY_RESTRICTION_SELECTION_TYPES.NO_RESTRICTIONS) {
                                setSelectedOptions?.({ ...selectedOptions, newSelectionType: item });
                            } else if (
                                item === CATEGORY_RESTRICTION_SELECTION_TYPES.ONLY_SELECTED &&
                                selectedOptions?.newSelectionType ===
                                CATEGORY_RESTRICTION_SELECTION_TYPES.NO_RESTRICTIONS
                            ) {
                                const myOptions = { ...selectedOptions };
                                delete myOptions.newSelectionType;
                                setSelectedOptions?.({ ...myOptions });
                            } else {
                                showAlert?.('Die Daten können nicht bearbeitet werden.', ALERT_SEVERITY.WARNING);
                            }
                        } else {
                            setSelectedOptions?.({ ...selectedOptions, selectionType: item });
                        }
                    }}
                />

                {mySelectionType === CATEGORY_RESTRICTION_SELECTION_TYPES.ONLY_SELECTED && (
                    <>
                        <RadioButtonGroup
                            items={this.categoryScopeTypes}
                            selectedKey={selectedOptions?.scopeType}
                            onSelect={(item) => {
                                if (!editMode) {
                                    setSelectedOptions?.({ ...selectedOptions, scopeType: item });
                                } else {
                                    showAlert?.('Die Daten können nicht bearbeitet werden.', ALERT_SEVERITY.WARNING);
                                }
                            }}
                        />

                        {(selectedCategories?.length ?? 0) > 0 && (
                            <PaginationWrapper
                                items={selectedCategories ?? []}
                                pageSize={5}
                                threshold={5}
                                disablePaginationOnSearch={true}
                                showSearch={(selectedCategories?.length ?? 0) > 5}
                                filterFn={(cat, term) => {
                                    const name = this.normalize(cat?.name);
                                    const id = this.normalize(cat?.id);
                                    const shop = this.normalize(cat?.shopInfo?.name);
                                    return name.includes(term) || id.includes(term) || shop.includes(term);
                                }}
                            >
                                {(pagedItems) => (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {pagedItems.map((cat) => {
                                            const textColor = cat.deleted ? '#979797' : '#424242';
                                            const textDecoration = cat.deleted ? 'line-through' : 'none';

                                            return (
                                                <DeletableChip
                                                    key={`CAT_${cat.id}`}
                                                    disabled={
                                                        editMode &&
                                                        !cat.added &&
                                                        selectedOptions?.scopeType === CATEGORY_SCOPE_TYPE.VALID_FOR
                                                    }
                                                    onDelete={() => this.deleteById(cat.id)}
                                                    showAlert={showAlert}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            gap: 0.75,
                                                            py: 0.75,
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                color: textColor,
                                                                fontWeight: 'bold',
                                                                fontSize: 14,
                                                                textDecoration,
                                                                wordBreak: 'break-word',
                                                            }}
                                                        >
                                                            Kategorie: {cat.name} ({cat.id})
                                                        </Typography>

                                                        <Typography
                                                            sx={{
                                                                color: textColor,
                                                                fontSize: 14,
                                                                textDecoration,
                                                                wordBreak: 'break-word',
                                                            }}
                                                        >
                                                            Shop: {cat.shopInfo?.name}, {cat.shopInfo?.street} {cat.shopInfo?.houseNumber}, {cat.shopInfo?.zip} {cat.shopInfo?.city}
                                                        </Typography>

                                                        {selectedOptions?.scopeType === CATEGORY_SCOPE_TYPE.VALID_FOR &&
                                                            (
                                                                editMode && !cat.added
                                                                    ? cat.minQty != null && (
                                                                        <Typography
                                                                            sx={{
                                                                                color: textColor,
                                                                                fontSize: 14,
                                                                                textDecoration,
                                                                            }}
                                                                        >
                                                                            Mindestmenge: {cat.minQty}
                                                                        </Typography>
                                                                    )
                                                                    : (!editMode || this.state.mustHaveMinQty) && (
                                                                        <Box
                                                                            sx={{
                                                                                display: 'flex',
                                                                                flexDirection: 'row',
                                                                                justifyContent: 'center',
                                                                            }}
                                                                        >
                                                                            <DeletableNumericInput
                                                                                maxLength={2}
                                                                                value={cat.minQty}
                                                                                setValue={(val) =>
                                                                                    setSelectedCategories?.(
                                                                                        selectedCategories.map((c) => {
                                                                                            if (this.normalize(c.id) === this.normalize(cat.id)) {
                                                                                                return { ...c, minQty: val };
                                                                                            }
                                                                                            return c;
                                                                                        })
                                                                                    )
                                                                                }
                                                                                placeholder="Mindestmenge"
                                                                                showPlaceholderTitle
                                                                            />
                                                                        </Box>
                                                                    )
                                                            )}

                                                        {cat.deleted && (
                                                            <Box
                                                                onClick={() => this.reselectById(cat.id)}
                                                                sx={{
                                                                    backgroundColor: '#dffbdf',
                                                                    mt: 0.75,
                                                                    borderRadius: '6px',
                                                                    p: 1,
                                                                    cursor: 'pointer',
                                                                    alignSelf: 'flex-start',
                                                                }}
                                                            >
                                                                <Typography
                                                                    sx={{
                                                                        color: '#4d714d',
                                                                        textAlign: 'center',
                                                                        fontSize: 14,
                                                                    }}
                                                                >
                                                                    Wiederherstellen
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </DeletableChip>
                                            );
                                        })}
                                    </Box>
                                )}
                            </PaginationWrapper>
                        )}

                        <CustomLightButton
                            label="Kategorie hinzufügen"
                            icon={require('../../../../../assets/icons/categories.png')}
                            onPress={() => {
                                this.props.onCategorySelectionPressed?.();
                            }}
                        />
                    </>
                )}
            </Box>
        );
    }
}

export default CategoryConfigSection;